/** Client chat payment bubble display helpers (WhatsApp-style: Pay / Completed / Expired). */

export type ChatPaymentDisplayStatus = 'pay' | 'completed' | 'expired';

export function formatPaymentAmount(amount: number, currency: string): string {
  const symbol = currency === 'USD' ? '$' : 'C$';
  return `${symbol}${Number(amount).toFixed(2)}`;
}

export function payeeDisplayName(metadata: Record<string, unknown> | undefined): string {
  if (metadata?.payee_type === 'service_provider' && metadata?.provider_name) {
    return String(metadata.provider_name);
  }
  return 'Canadawale Yar™';
}

/** Active request: "To …" */
export function payeeLabel(metadata: Record<string, unknown> | undefined): string {
  return `To ${payeeDisplayName(metadata)}`;
}

/** Completed bubble: "Sent to …" */
export function sentToLabel(metadata: Record<string, unknown> | undefined): string {
  return `Sent to ${payeeDisplayName(metadata)}`;
}

/** True when stored expires_at is in the past (client can hide Pay before webhook). */
export function isExpiresAtPast(expiresAt?: string | null): boolean {
  if (!expiresAt) return false;
  const ms = Date.parse(expiresAt);
  if (Number.isNaN(ms)) return false;
  return ms <= Date.now();
}

/**
 * Client-facing status:
 * - confirmed/verified → Completed
 * - expired/cancelled/failed OR past expires_at → Expired
 * - otherwise → Pay (no “Pending” label)
 */
export function resolveChatPaymentDisplayStatus(
  paymentStatus?: string | null,
  expiresAt?: string | null
): ChatPaymentDisplayStatus {
  if (paymentStatus === 'verified' || paymentStatus === 'confirmed') {
    return 'completed';
  }
  if (
    paymentStatus === 'expired' ||
    paymentStatus === 'cancelled' ||
    paymentStatus === 'failed' ||
    isExpiresAtPast(expiresAt)
  ) {
    return 'expired';
  }
  return 'pay';
}

export function chatPaymentFooterLabel(display: ChatPaymentDisplayStatus): string {
  switch (display) {
    case 'completed':
      return 'Completed';
    case 'expired':
      return 'Expired';
    default:
      return 'Pay';
  }
}
